import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { Typography, Button, Tooltip, Skeleton } from 'antd';
import { RefreshCw, Layers } from 'lucide-react';

import { CreativityContentData } from '../../types/banner';
import { MenuSections } from '../../types/menu-sections';
import { tokens } from '../../theme/tokens';
import { useModal } from '../../context/modal';

import { getDisplayContent, getBannerStatusData, addCacheBusting, getFirstImage } from '../../utils';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { AppCard } from '../ui/layout/app-card';
import { StatusTag } from '../ui/status-tag';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Text } = Typography;

interface RecentBannersListProps {
  items: CreativityContentData[];
  onRefresh: () => void;
  loading?: boolean;
  onNavigate?: (section: MenuSections) => void;
}

export const RecentBannersList: React.FC<RecentBannersListProps> = ({
  items,
  onRefresh,
  loading = false,
  onNavigate
}) => {
  const { openAsset } = useModal();
  const { fontSize, spacing } = useResponsiveSizes();

  return (
    <div style={{ flex: 1 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Title
          style={{
            fontSize: fontSize.xl,
            fontWeight: 500,
            color: tokens.colors.primary.neutral[1010]
          }}
        >
          Últimos assets
        </Typography.Title>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <Tooltip title='Refrescar' mouseEnterDelay={0.3}>
            <Button type='text' icon={<RefreshCw size={18} />} onClick={() => onRefresh()} loading={loading} />
          </Tooltip>
          <Tooltip title='Ir a la biblioteca' mouseEnterDelay={0.3}>
            <Button type='text' onClick={() => onNavigate?.('library')} icon={<Layers size={18} />} />
          </Tooltip>
        </div>
      </div>

      {/* Lista de banners */}
      <AppCard styles={{ body: { padding: `${spacing.sm}px ${spacing.sm}px` } }}>
        {items.map((item, index) => {
          const imageUrl = getFirstImage(item);
          const imageUrlWithCache = imageUrl ? addCacheBusting(imageUrl, item.updatedAt ?? item.date) : undefined;
          const showImage = item.status === 'DONE' && imageUrl;
          const statusData = getBannerStatusData(item);

          return (
            <Button
              key={item.sk}
              style={{
                width: '100%',
                height: 'auto',
                paddingTop: spacing.sm,
                paddingBottom: spacing.sm,
                paddingRight: spacing.sm,
                paddingLeft: spacing.md,
                marginBottom: index < items.length - 1 ? spacing.xs : 0,
                textAlign: 'left',
                background: 'transparent',
                borderRadius: '8px',
                boxShadow: 'none',
                border: 'none',
                display: 'flex',
                justifyContent: 'space-between',
                cursor: 'pointer',
                alignItems: 'flex-start'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onClick={() => openAsset(item.bannerId)}
            >
              {/* Contenido principal: imagen + info */}
              <div
                style={{
                  display: 'flex',
                  gap: spacing.md,
                  flex: 1,
                  minWidth: 0,
                  alignItems: 'center',
                  maxWidth: 'calc(100% - 150px)'
                }}
              >
                {/* Preview thumbnail */}
                <div
                  style={{
                    width: spacing.xxl + 20,
                    height: spacing.xxl,
                    flexShrink: 0,
                    position: 'relative'
                  }}
                >
                  {showImage ? (
                    <img
                      src={imageUrlWithCache}
                      alt='Banner preview'
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        opacity: 0.9,
                        boxShadow: '0 0 10px rgba(102, 126, 234, 0.15)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.opacity = '0.95';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(102, 126, 234, 0.25)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.opacity = '0.85';
                        e.currentTarget.style.boxShadow = '0 0 10px rgba(102, 126, 234, 0.15)';
                      }}
                    />
                  ) : (
                    <Skeleton.Image
                      style={{
                        width: spacing.xxl + 20,
                        height: spacing.xxl,
                        borderRadius: '6px'
                      }}
                    />
                  )}
                </div>

                {/* Info del banner */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    alignItems: 'flex-start',
                    flex: 1
                  }}
                >
                  <Text
                    ellipsis
                    style={{
                      fontSize: fontSize.xlg,
                      fontWeight: 500,
                      color: tokens.colors.primary.neutral[1010],
                      width: '100%',
                      display: 'block'
                    }}
                  >
                    {getDisplayContent(item)}
                  </Text>

                  <Text style={{ fontSize: fontSize.base, color: '#888' }}>{dayjs(item.createdAt).fromNow()}</Text>
                </div>
              </div>

              {/* Tag de estado  */}

              {statusData && <StatusTag label={statusData.label} size='medium' />}
            </Button>
          );
        })}
      </AppCard>
    </div>
  );
};

export default RecentBannersList;
